/**
 * Imperative ProseMirror mount bound to a Loro container (I12: React never re-renders the
 * editor from state). Verified names from loro-prosemirror 0.4.4: `LoroSyncPlugin({doc,
 * containerId})`, `LoroUndoPlugin({doc, undoManager})`, `LoroEphemeralCursorPlugin(store, {})`,
 * `CursorEphemeralStore`.
 *
 * Several views of one container in one tab (a block and its mirror, spec §8.18, spike S13):
 * loro-prosemirror ignores local events other than undo, so a view would never see another view's
 * typing. Each view therefore records itself as the writer while dispatching (Loro emits events
 * synchronously inside `commit`) and rebuilds its state from Loro, with fresh plugin instances, when a
 * local batch it did not write touches its container.
 */
import { baseKeymap, toggleMark } from "prosemirror-commands";
import { inputRules, wrappingInputRule, textblockTypeInputRule } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { EditorState, type Plugin } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { splitListItem, liftListItem, sinkListItem } from "prosemirror-schema-list";
import { LoroSyncPlugin, LoroUndoPlugin, LoroEphemeralCursorPlugin, CursorEphemeralStore, undo, redo, type LoroDocType } from "loro-prosemirror";
import type { ContainerID, LoroDoc, LoroEventBatch, UndoManager } from "loro-crdt";
import { pmSchema } from "./pmSchema";
import { toolbarPlugin } from "./PmToolbar";
import { translate, type Translate } from "../i18n/core";

export interface PmMountOptions {
  doc: LoroDoc;
  containerId: ContainerID;
  /** null for a hidden helper view (Markdown mode): it must not take over the shared undo manager */
  undoManager: UndoManager | null;
  /** null: publish no cursor (a hidden view's selection means nothing to other readers) */
  presence: CursorEphemeralStore | null;
  readOnly: boolean;
  /** the formatting toolbar at the top (WYSIWYM mode, spec §8.19) */
  toolbar?: boolean;
  /** translates the toolbar (spec §8.23); English when absent */
  t?: Translate;
  onExitUp?: () => void;
  onExitDown?: () => void;
  onFocus?: () => void;
}

function rules() {
  const s = pmSchema;
  return inputRules({
    rules: [
      wrappingInputRule(/^\s*>\s$/, s.nodes.blockquote!),
      wrappingInputRule(/^(\d+)\.\s$/, s.nodes.ordered_list!, (m) => ({ order: +m[1]! }), (m, node) => node.childCount + node.attrs.order === +m[1]!),
      wrappingInputRule(/^\s*([-+*])\s$/, s.nodes.bullet_list!),
      textblockTypeInputRule(/^(#{1,6})\s$/, s.nodes.heading!, (m) => ({ level: m[1]!.length })),
    ],
  });
}

/** The view currently committing into each document (set only during its dispatch). */
const writers = new WeakMap<LoroDoc, EditorView>();

/** An account colour, or nothing: it ends up in a style attribute. */
const safeColour = (c: unknown): string | null => (typeof c === "string" && /^#[0-9a-f]{3,8}$/i.test(c) ? c : null);

/**
 * Other readers' carets and selections (spec §8.12), drawn in their account colour. The plugin's own
 * caret puts the name on that colour, which an amber or a pale account colour leaves unreadable; here
 * the name sits on the surface in the text colour, framed in the reader's colour (FRONTEND_DESIGN.md §4.11).
 */
function remoteCarets(store: CursorEphemeralStore) {
  const userOf = (peer: string) => store.getAll()[peer as `${number}`]?.user;
  return LoroEphemeralCursorPlugin(store, {
    // the caret holds no text: its name is CSS generated content (`::after`), so another reader's
    // caret never shows up in the editor's text, its copy or a screen reader's reading of it
    createCursor: (peer) => {
      const user = userOf(peer);
      const caret = document.createElement("span");
      caret.className = "kss-caret";
      caret.style.borderColor = safeColour(user?.color) ?? "var(--color-accent)";
      caret.dataset.name = user?.name ?? "";
      caret.setAttribute("aria-hidden", "true");
      return caret;
    },
    createSelection: (peer) => {
      const colour = safeColour(userOf(peer)?.color) ?? "var(--color-accent)";
      return { class: "kss-remote-selection", style: `background-color: color-mix(in srgb, ${colour} 22%, transparent)` };
    },
  });
}

export function mountProseMirror(parent: HTMLElement, opts: PmMountOptions): EditorView {
  const s = pmSchema;
  const plugins = (): Plugin[] => [
    rules(),
    keymap({
      "Mod-b": toggleMark(s.marks.strong!),
      "Mod-i": toggleMark(s.marks.em!),
      "Mod-`": toggleMark(s.marks.code!),
      "Mod-Shift-x": toggleMark(s.marks.strike!),
      Enter: splitListItem(s.nodes.list_item!),
      "Shift-Tab": liftListItem(s.nodes.list_item!),
      Tab: sinkListItem(s.nodes.list_item!),
      "Mod-z": undo,
      "Mod-y": redo,
      "Mod-Shift-z": redo,
      ArrowUp: (state) => {
        if (opts.onExitUp && state.selection.$from.pos <= 1) {
          opts.onExitUp();
          return true;
        }
        return false;
      },
      ArrowDown: (state) => {
        if (opts.onExitDown && state.selection.$to.pos >= state.doc.content.size - 1) {
          opts.onExitDown();
          return true;
        }
        return false;
      },
    }),
    keymap(baseKeymap),
    // loro-prosemirror types its doc as LoroDocType (root `doc`/`data` maps); with containerId it
    // binds to our nested map instead, so the cast is safe.
    LoroSyncPlugin({ doc: opts.doc as unknown as LoroDocType, containerId: opts.containerId }),
    ...(opts.undoManager ? [LoroUndoPlugin({ doc: opts.doc as unknown as LoroDocType, undoManager: opts.undoManager })] : []),
    ...(opts.presence ? [remoteCarets(opts.presence)] : []),
    ...(opts.toolbar && !opts.readOnly ? [toolbarPlugin(opts.t ?? ((key, params) => translate("en", key, params)))] : []),
  ];
  const view: EditorView = new EditorView(parent, {
    state: EditorState.create({ schema: s, plugins: plugins() }),
    editable: () => !opts.readOnly,
    dispatchTransaction(tr) {
      const previous = writers.get(opts.doc);
      writers.set(opts.doc, view);
      try {
        view.updateState(view.state.apply(tr));
      } finally {
        if (previous) writers.set(opts.doc, previous);
        else writers.delete(opts.doc);
      }
    },
    handleDOMEvents: {
      focus: () => {
        opts.onFocus?.();
        return false;
      },
    },
  });

  const path = opts.doc.getPathToContainer(opts.containerId) ?? [];
  let rebuildQueued = false;
  const unsubscribe = opts.doc.subscribe((batch: LoroEventBatch) => {
    if (batch.by !== "local" || batch.origin === "undo" || writers.get(opts.doc) === view || rebuildQueued) return;
    if (!batch.events.some((e) => path.length <= e.path.length && path.every((p, i) => e.path[i] === p))) return;
    rebuildQueued = true;
    queueMicrotask(() => {
      rebuildQueued = false;
      if (!view.isDestroyed) view.updateState(EditorState.create({ schema: s, plugins: plugins() }));
    });
  });
  const destroy = view.destroy.bind(view);
  view.destroy = () => {
    unsubscribe();
    destroy();
  };
  return view;
}
